import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { PlotlyViaWindowModule } from 'angular-plotly.js';
import { Layout, PlotData } from 'plotly.js';
import { ShakerProfileDialogComponent, ShakerProfileDialogResult } from 'src/app/components/dialogs/shaker-control/shaker-profile-dialog/shaker-profile-dialog.component';

import { DialogService } from 'src/app/components/dialogs/dialog/dialog.service';
import { SafeResizeHandlerDirective } from 'src/app/directives/plotly/reset-zoom-buttons/safe-resize-handler.directive';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { ShakerControlSequence, isShakerControlSequence } from 'src/app/model/shaker-control/shaker-control-sequence';
import { IUserSetting_ShakerControlProfile } from 'src/app/model/shaker-control/shaker-control-userdata-template-types';
import { ShakerFeatureDescriptor } from 'src/app/model/shaker-control/shaker-feature-descriptor';
import { IUserSetting } from 'src/app/model/thingie/task';
import { LogService } from 'src/app/services/loggers/logger.service';
import { ShakerProfileFeatureService } from 'src/app/services/shaker-profile/shaker-profile-feature.service';
import { UserDataService } from 'src/app/services/user-data/user-data.service';
import { deepCopy, deepCopySync } from 'src/app/utility/deep-copy';
import { loadPlotly } from 'src/app/utility/plotly';
import { TIME_DESCRIPTOR } from 'src/app/utility/shaker-control/shaker-control-sequence-time-descriptor';
import { convertShakerSequenceToPlotData, getShakerSequencePlotLayoutAndFeatureToAxisMapping, getShakerSequencePlotLayoutBase } from 'src/app/utility/shaker-control/shaker-control-sequence-to-graph';
import { ProfileOrCustomIndicator, ShakerControlProfileSelectionComponent } from '../shaker-control-profile-selection/shaker-control-profile-selection.component';


export type ShakerProfileResult = NonNullable<ShakerProfileDialogResult>;


type ConfiguredValues = {
  sequence: ShakerControlSequence;
  initialValues: ShakerControlSequence;
};


type CustomProfileCache = {

  /**
   * Values of the custom profile
   */
  configuredValues: ConfiguredValues;

  /**
   * ID's of features used
   */
  features: ShakerFeatureDescriptor[];
};


@Component({
  selector: 'app-shaker-control-task-configuration',
  imports: [
    ShakerControlProfileSelectionComponent,
    SafeResizeHandlerDirective,
    PlotlyViaWindowModule,
    MaterialImportsModule,
  ],
  templateUrl: './shaker-control-task-configuration.component.html',
  styleUrls: ['./shaker-control-task-configuration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShakerControlTaskConfigurationComponent implements OnInit {

  /**
   * @private
   */
  protected readonly overrideHintText = `
    Different values in individual replicates.
    Changes will override replicate configurations.
  `;


  /**
   * @private
   */
  @ViewChild('plotWrapper', { static: true, read: ElementRef })
  private set _plotWrapper(val: ElementRef) {
    if (!this.plotWrapper) {
      this.plotWrapper = val.nativeElement;
    }
  }


  /**
   * Element surrounding the plotly element.
   * Used to determine the view dimensions for calculating the spacing factor
   * for the y-axes. {@link determineYAxesSpacingFactor}
   */
  private plotWrapper?: HTMLElement;


  /**
   * When true, the user can only change the values of this task by confirming
   * the override.
   */
  @Input()
  public set requireOverride(val: BooleanInput) {
    this.requireOverrideConfirmation = coerceBooleanProperty(val);
    this.overrideConfirmed = false;
  }


  /**
   * The value replacing the current value of the task, after the user has
   * confirmed the override.
   *
   * @throws if `val` is not a valid {@link ConfiguredValues} structure
   */
  @Input()
  public set overrideDefaultValue(val: any | undefined) {
    if (val === undefined) {
      this._overrideDefaultValue = undefined;
      return;
    }

    if (
      typeof val !== 'object'
      || !val.sequence || !val.initialValues || !val.features
      || !isShakerControlSequence(val.sequence)
      || !isShakerControlSequence(val.initialValues)
    ) {
      throw new Error('overrideDefaultValue provided value invalid');
    }

    this._overrideDefaultValue = val;
  }

  /**
   * Whether to display this component in readonly mode
   */
  @Input()
  public readonly = false;


  /**
   * The ID of the template to use as the default for this task when overriding.
   */
  @Input()
  public overrideDefaultTemplateId: string | undefined = undefined;


  /**
   * Reference to the user-setting input to configure.
   */
  @Input({ required: true })
  public set userSettingInput(val: IUserSetting) {
    if (this.userSettingInputStore) {
      throw new Error('userSettingInput should not be changed after being set.');
    }

    this.userSettingInputStore = val as IUserSetting_ShakerControlProfile;
  }


  /**
   * Reference to the user-setting input to configure.
   *
   * Available after the first OnChanges lifecycle hook finishes executing.
   *
   * @private
   */
  public get userSettingInput(): IUserSetting_ShakerControlProfile {
    return this.userSettingInputStore;
  }


  /**
   * Backing store for {@link userSettingInput}.
   *
   * Holds a reference to the user-setting input to configure.
   *
   * Available after the first OnChanges lifecycle hook finishes executing.
   *
   * Should not be used directly. Use {@link userSettingInput} instead.
   */
  protected userSettingInputStore!: IUserSetting_ShakerControlProfile;


  /**
   * Emits when the profile has been changed and written to the input.
   */
  @Output()
  public userSettingInputUpdate = new EventEmitter<void>();


  /**
   * Whether the user has to confirm value override, in case different tasks
   * of this type have different inputs.
   *
   * @private
   */
  protected requireOverrideConfirmation = false;


  /**
   * Whether the user has confirmed the override with the default value.
   *
   * @private
   */
  protected overrideConfirmed = false;


  /**
   * @private
   */
  private _overrideDefaultValue: (ConfiguredValues & { features: string[] }) | undefined = undefined;


  /**
   * Data shown in the plot
   *
   * @private
   */
  protected plotData: Partial<PlotData>[] = [];


  /**
   * Layout for the plot
   *
   * @private
   */
  protected plotLayout: Partial<Layout> = getShakerSequencePlotLayoutBase();


  /**
   * The Logger
   */
  private log = inject(LogService);


  /**
   * Shaker-feature service
   */
  private shakerFeatureService = inject(ShakerProfileFeatureService);


  /**
   * Feature-descriptors for shaker-feature types.
   */
  private readonly features$: Promise<ShakerFeatureDescriptor[]> = this
    .shakerFeatureService
    .getFeatures()
    .then(features => [...features, TIME_DESCRIPTOR])
    .catch(err => {
      this.log.error('Failed to load shaker feature list', err);
      return [];
    });


  /**
   * Shaker control profile dialog
   */
  private dialogService = inject(DialogService);


  /**
   * Change detector for the component
   */
  private changeDetector = inject(ChangeDetectorRef);


  /**
   * Represent the features that are picked by the user in the shaker profile dialog.
   */
  private pickedFeatures: ShakerFeatureDescriptor[] = [];


  /**
   * Storage for values as they were returned from the dialog.
   */
  private configuredValues: ConfiguredValues = {
    sequence: [],
    initialValues: []
  };


  /**
   * Cache for the custom profile corresponding to the custom profile indicator
   * {@link UserDataService.customTemplateIndicator}.
   */
  private customProfileCache: CustomProfileCache = {
    configuredValues: { sequence: [], initialValues: [] },
    features: []
  };

  constructor() {
    void loadPlotly();
  }


  /**
   * @private
   */
  protected async _setSelectedProfile(profile: ProfileOrCustomIndicator) {
    if (typeof profile === 'string') {
      this.selectedProfileId = profile;
    } else {
      this.selectedProfileId = profile._id!;
    }

    if (profile === UserDataService.customTemplateIndicator) {
      this.configuredValues = deepCopySync(this.customProfileCache.configuredValues);
      this.pickedFeatures = deepCopySync(this.customProfileCache.features);
    } else {
      const profileFeatures = profile.value.template.payload.value.features;
      await this.features$.then(available => {
        this.pickedFeatures = available.filter(f => profileFeatures.includes(f.id));
      });

      this.configuredValues = deepCopySync(profile.value.template.payload.value);
    }

    this.transferValuesToUserSettingInput();
    this.userSettingInputUpdate.emit();

    // Re-generate the graph from the results
    void this.updateGraphFromConfiguredValues();
  }


  /**
   * Backing store for the currently-selected profile's ID.
   *
   * @see {@link _setSelectedProfile}.
   * @private
   */
  protected selectedProfileId: string = UserDataService.customTemplateIndicator;


  /**
   * @private
   */
  public async ngOnInit() {
    // Populate this component instance from the input and render the graph
    const input = this.userSettingInput;

    if (input.templateId !== undefined) {
      // Extract the template & values

      await this.features$.then(available => {
        this.pickedFeatures = available.filter(f => input.value.features.includes(f.id));
      });

      this.configuredValues.initialValues = input.value.initialValues;
      this.configuredValues.sequence      = input.value.sequence;
      this.selectedProfileId             = input.templateId;

      if (input.templateId === UserDataService.customTemplateIndicator) {
        this.customProfileCache = {
          configuredValues: deepCopySync(this.configuredValues),
          features: deepCopySync(this.pickedFeatures)
        };
      }

      await this.updateGraphFromConfiguredValues();
    }
  }


  /**
   * Show the shaker profile dialog and update the graph with the results
   * after it is closed.
   */
  protected async _editShakerProfile(): Promise<void> {
    const initialValues = await deepCopy(this.configuredValues.initialValues);
    const sequence      = await deepCopy(this.configuredValues.sequence);

    const result: ShakerProfileDialogResult = await this.dialogService.open({
      component: ShakerProfileDialogComponent,
      data: {
        initialValues,
        sequence,
        readonly: false
      }
    }).afterClosed();

    if (!result) {
      return;
    }

    this.configuredValues = {
      sequence: result.sequence,
      initialValues: result.initialValues
    };
    this.selectedProfileId = result.profileId;
    await this.features$
      .then(featureList => {
        this.pickedFeatures = result.features.map(f => featureList.find(feat => feat.id === f)!);
      });


    if (result.profileId === UserDataService.customTemplateIndicator) {
      // Cache the custom profile, in case the user switches to a different, but
      // back to custom later on.
      this.customProfileCache = {
        configuredValues: deepCopySync(this.configuredValues),
        features: deepCopySync(this.pickedFeatures)
      };
    }


    // Re-generate the graph from the results
    this.plotData = await this.shakerSequenceToGraphData({
      sequence: this.configuredValues.sequence,
      initialValues: this.configuredValues.initialValues
    });


    this.transferValuesToUserSettingInput();
    this.userSettingInputUpdate.emit();


    // Update the UI
    this.changeDetector.markForCheck();
  }


  private async shakerSequenceToGraphData(input: {
    sequence: ShakerControlSequence;
    initialValues: ShakerControlSequence;
  }): Promise<Partial<PlotData>[]> {
    // Merge initial values and control sequence
    const mergedSequence = [...input.initialValues, ...input.sequence];

    // Get feature descriptors
    const features = await this.features$.then(f => [...f, TIME_DESCRIPTOR]);

    const viewSize = this.plotWrapper?.clientWidth ? { width: this.plotWrapper.clientWidth } : undefined;

    const { layout, mapping } = getShakerSequencePlotLayoutAndFeatureToAxisMapping(
      {
        sequence: mergedSequence,
        featureDescriptors: features
      },
      viewSize
    );

    this.plotLayout = layout;

    // Convert to plot data
    return convertShakerSequenceToPlotData(
      {
        sequence: mergedSequence,
        featureDescriptors: features
      }, mapping
    );
  }


  private transferValuesToUserSettingInput() {
    this.userSettingInput.value = {
      sequence: this.configuredValues.sequence,
      initialValues: this.configuredValues.initialValues,
      features: this.pickedFeatures.map(f => f.id)
    };

    this.userSettingInput.templateId = this.selectedProfileId;
  }


  private async updateGraphFromConfiguredValues() {
    const data = await this.shakerSequenceToGraphData({
      sequence: this.configuredValues.sequence,
      initialValues: this.configuredValues.initialValues
    });

    this.plotData = data;

    this.changeDetector.markForCheck();
  }


  /**
   * @private
   */
  protected _runOnce() {
    // Just here to trigger change detection once after the plot has finished initialization.
  }


  /**
   * @private
   */
  protected _confirmOverride() {
    if (!this.overrideDefaultValue || !this.overrideDefaultTemplateId) {
      throw new Error('Missing override values: orride value and template id need to be provided');
    }

    this.configuredValues = {
      initialValues: this.overrideDefaultValue.initialValues,
      sequence: this.overrideDefaultValue.sequence
    };
    this.selectedProfileId = this.overrideDefaultTemplateId!;

    void this.features$.then(available => {
      this.pickedFeatures = available.filter(f => this.overrideDefaultValue!.features.includes(f.id));
    });

    this.overrideConfirmed = true;

    this.transferValuesToUserSettingInput();
    this.userSettingInputUpdate.emit();

    this.changeDetector.markForCheck();
    setTimeout(() => void this.updateGraphFromConfiguredValues(), 0);
  }


  /**
   * Show the shaker profile dialog and update the graph with the results
   * after it is closed.
   */
  protected async _showReadonlyProfileDetails(): Promise<void> {
    const initialValues = await deepCopy(this.configuredValues.initialValues);
    const sequence      = await deepCopy(this.configuredValues.sequence);

    await this.dialogService.open({
      component: ShakerProfileDialogComponent,
      data: {
        initialValues,
        sequence,
        readonly: true
      }
    }).afterClosed();
  }
}
